import React, { useEffect, useState } from "react";
import "./styles.scss";
import { useNavigate } from "react-router-dom";
import { Checkbox, Tooltip } from "@enpowered/ui";
import classNames from "classnames";
import IconTextButton from "../../../common/IconTextButton";
import { ReactComponent as ArchiveIcon } from "assets/archive.svg";
import { archiveFinancingScenarios } from "../../../services/FinancingScenarios";
import { emitter } from "utils/emitter";
import SnackBar from "common/SnackBar";
import SearchField from "../../../common/SearchField";
import { getMixpanel } from "../../../services/mixpanel";
import { getStateLabel } from "../../../lib/constants";

function ProjectsTable({ projectList = [], refetchProjects }) {
  const PROJECT_ID = "Project ID";
  const navigate = useNavigate();
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [itemHovered, setItemHovered] = useState();
  const [message, setMessage] = useState({});
  const [filteredProjects, setFilteredProjects] = useState(projectList);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [searchString, setSearchString] = useState("");

  const PROJECTS_ARCHIVED_MESSAGE = "Project(s) archived!";

  const mixpanel = getMixpanel();

  const fieldsToSearch = ["projectName"];

  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
    /** @param {{ type: "info" | "warning" | "error" | "success", name: string, text: string }} msg */
    emitter.on("message", msg => setMessage(msg));
    return () => {
      emitter.off("message", msg => setMessage(msg));
    };
  }, []);

  projectList?.sort((a, b) => b.dateAdded.localeCompare(a.dateAdded));

  useEffect(() => {
    if (searchString.length >= 3) {
      setSelectedProjects([]);
      setIsFilterActive(true);
      setFilteredProjects(
        projectList.filter(item =>
          fieldsToSearch.some(
            field =>
              (item[field]
                ?.toLowerCase()
                ?.indexOf(searchString?.toLowerCase()) ?? -1) >= 0
          )
        )
      );
    } else if (isFilterActive) {
      setIsFilterActive(false);
    }
  }, [projectList, searchString]);

  const archive = financingScenarioIds =>
    archiveFinancingScenarios(financingScenarioIds)
      .then(() => {
        setMessage({
          text: PROJECTS_ARCHIVED_MESSAGE,
          type: "success"
        });
        setSelectedProjects([]);
        refetchProjects();
      })
      .catch(err =>
        setMessage({
          text: err?.message ?? err,
          type: "error"
        })
      );

  return (
    <div className="projectsTable">
      <SnackBar
        messages={message?.text ? [message.text] : []}
        type={message?.type}
        actionContent={
          message?.text === PROJECTS_ARCHIVED_MESSAGE ? (
            <button
              className="s16 font-semibold ml-12 mt-1 mr-2"
              onClick={() => {
                mixpanel.track("View Archive (Snackbar)");
                navigate(`/projects/archive`);
              }}
            >
              View Archive
            </button>
          ) : (
            ""
          )
        }
      />
      <div className="tableActions mb-4">
        <SearchField
          searchString={searchString}
          setSearchString={setSearchString}
          listItemName="Projects"
        />
        {selectedProjects.length > 0 && (
          <IconTextButton
            icon={<ArchiveIcon />}
            className="borderGrayLighter round"
            label={`Archive project${selectedProjects.length > 1 ? "s" : ""}`}
            onClick={() => {
              mixpanel.track("Archive Button Clicked", {
                "Select All":
                  selectedProjects.length === projectList?.length &&
                  selectedProjects.length > 0,
                "Project IDs": selectedProjects
              });
              archive(selectedProjects);
            }}
          />
        )}
      </div>
      <table>
        <thead>
          <tr>
            <th>
              {(isFilterActive ? filteredProjects : projectList)?.length >
                0 && (
                <Tooltip
                  placement="top"
                  title={
                    selectedProjects.length > 0 ? "Deselect all" : "Select All"
                  }
                >
                  <Checkbox
                    id="selectAllProjects"
                    name="selectAllProjects"
                    checked={
                      selectedProjects.length === projectList?.length &&
                      selectedProjects.length > 0
                    }
                    defaultChecked={false}
                    size="small"
                    className={classNames("mx-1 border align-middle", {
                      "bg-white":
                        selectedProjects.length !== projectList?.length,
                      "bg-en-yellow-100":
                        selectedProjects.length === projectList?.length
                    })}
                    onChange={() =>
                      selectedProjects.length > 0
                        ? setSelectedProjects([])
                        : setSelectedProjects(
                            projectList?.map(p => p.financingScenarioId)
                          )
                    }
                  />
                </Tooltip>
              )}
            </th>
            <th>Project Name</th>
            <th>Project Type</th>
            <th>Financing Option</th>
            <th>Location</th>
            <th>
              <div className="w-10"></div>
            </th>
          </tr>
        </thead>
        <tbody>
          {(isFilterActive ? filteredProjects : projectList)?.map(
            (project, i) => {
              let isSelected = selectedProjects.includes(
                project.financingScenarioId
              );
              return (
                <tr
                  key={`projectsTable-${i}`}
                  onClick={e => {
                    if (
                      ["svg", "path"].includes(
                        e.target?.nodeName?.toLowerCase()
                      ) ||
                      e.target.className === "checkboxTd"
                    )
                      return;
                    mixpanel.track("Project Selected", {
                      [PROJECT_ID]: project.financingScenarioId,
                      Partner: project.partnerCompanyName
                    });
                    navigate(
                      `/project/${project.financingScenarioId}${location.search}`
                    );
                  }}
                  onMouseOver={() =>
                    setItemHovered(project.financingScenarioId)
                  }
                  onMouseLeave={() => setItemHovered(null)}
                  className={classNames({ rowSelected: isSelected })}
                >
                  <td className="checkboxTd">
                    <Checkbox
                      id={`checkbox-${i}`}
                      name={`checkbox-${i}`}
                      checked={isSelected}
                      defaultChecked={false}
                      size="small"
                      className={classNames("mx-1 border align-middle", {
                        "bg-white": !isSelected,
                        "bg-en-yellow-100": isSelected
                      })}
                      onChange={() => {
                        selectedProjects.some(
                          id => id === project.financingScenarioId
                        )
                          ? setSelectedProjects(
                              selectedProjects.filter(
                                p => p != project.financingScenarioId
                              )
                            )
                          : setSelectedProjects(
                              selectedProjects.concat(
                                project.financingScenarioId
                              )
                            );
                      }}
                    />
                  </td>
                  <td>{project.projectName}</td>
                  <td>{project.projectType}</td>
                  <td>Loan</td>
                  <td>
                    {[
                      getStateLabel(project.countryCode, project.state),
                      project.countryCode
                    ]
                      .filter(field => !!field)
                      .join(", ")}
                  </td>
                  <td className="actionsTd">
                    {itemHovered === project.financingScenarioId && (
                      <Tooltip placement="top" title={"Archive Project"}>
                        <button
                          onClick={() => {
                            archive([project.financingScenarioId]);
                            mixpanel.track("Archive Action Button Clicked", {
                              "Project ID": project.financingScenarioId
                            });
                          }}
                        >
                          <ArchiveIcon />
                        </button>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              );
            }
          )}
        </tbody>
      </table>
    </div>
  );
}

export default ProjectsTable;
